import { forwardRef, memo, type Ref, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { getUserEducation } from '~/actions';
import { FormDropdown } from '~/commonComponents/FormDropdown';
import { DropdownOption } from '~/commonComponents/FormDropdown/DropdownOption';
import { UserProfession } from '~/enums/UserProfession';
import { getShouldShowFrontDeskSpecialty } from '~/growthbook';
import { useAppSelector } from '~/hooks/useAppSelector';
import { filterObjectKeys } from '~/utils/Object';

interface IPreferredSpecialtyFieldProps {
  value?: string;
  onChange?: (value: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  professionType: string;
}

const Component = (
  { value, onChange, isOpen, onToggle, professionType }: IPreferredSpecialtyFieldProps,
  ref: Ref<HTMLDivElement>,
) => {
  const dispatch = useDispatch();

  const userEducation = useAppSelector((state) => state.user.userEducation);

  useEffect(() => {
    if (!professionType) return;
    const code = UserProfession.getCode(professionType) as string | undefined;
    if (!code) return;
    const professionCode = UserProfession.getNormalizedCode(code);
    dispatch(getUserEducation({ profession: professionCode }));
  }, [dispatch, professionType]);

  const normalizedCode = useMemo(() => {
    if (!professionType) return undefined;
    const code = UserProfession.getCode(professionType) as string | undefined;
    if (!code) return undefined;
    return UserProfession.getNormalizedCode(code) as string;
  }, [professionType]);

  const specialtiesAndProcedures = normalizedCode
    ? (userEducation as any)?.[normalizedCode]?.specialtiesAndProcedures
    : undefined;

  const isLoadingSpecialties = !!professionType && !specialtiesAndProcedures;

  const specialties = useMemo(() => {
    if (!specialtiesAndProcedures) return [];

    const filteredSpecialties = filterObjectKeys(specialtiesAndProcedures, 'specialty');
    const keys = Object.keys(filteredSpecialties);

    const showFrontDesk = getShouldShowFrontDeskSpecialty();

    return showFrontDesk ? keys : keys.filter((key) => key !== 'specialty_Front Desk');
  }, [specialtiesAndProcedures]);

  useEffect(() => {
    if (value || specialties.length === 0) return;
    const generalDentistry = specialties.find((s) => s === 'specialty_General Dentistry');
    if (generalDentistry) {
      onChange?.(generalDentistry);
    }
  }, [specialties, value, onChange]);

  const displayValue = value ? value.split('_')[1] : undefined;

  return (
    <div ref={ref}>
      <FormDropdown
        label="Preferred Specialty"
        value={displayValue}
        isOpen={isOpen}
        isLoading={isLoadingSpecialties}
        disabled={!professionType}
        onToggle={onToggle}
        testId="preferred-specialty-field"
      >
        {specialties.map((specialty) => (
          <DropdownOption
            key={specialty}
            isSelected={value === specialty}
            onSelect={() => {
              onChange?.(specialty);
              onToggle();
            }}
            testId={`specialty-option-${specialty}`}
          >
            {specialty.split('_')[1]}
          </DropdownOption>
        ))}
      </FormDropdown>
    </div>
  );
};

export const PreferredSpecialtyField = memo(forwardRef(Component));
